import {
  GoogleAuthError,
  GOOGLE_GMAIL_MODIFY_SCOPE,
  GOOGLE_GMAIL_READONLY_SCOPE,
  GOOGLE_GMAIL_SCOPE,
  ensureGoogleAccessToken,
  getStoredToken,
  requestAccessTokenSilent,
  resolveRefreshScope,
} from "@/lib/googleAuth";
import { stripHtml } from "@/lib/citationUtils";
import { looksLikeHtml, parseGmailPayload } from "@/lib/gmailMime";
import { buildGmailSearchQuery } from "@/lib/gmailSearchQuery";
import type { CommunicationListParams } from "@/types/communications";
import type { GmailMessage } from "@/types/gmail";

const GMAIL_API = "https://gmail.googleapis.com/gmail/v1";

const LABEL_MAP: Record<string, string> = {
  inbox: "INBOX",
  sent: "SENT",
  starred: "STARRED",
};

function extractHeader(
  headers: Array<{ name?: string; value?: string }>,
  name: string
): string {
  return headers?.find((h) => h.name?.toLowerCase() === name.toLowerCase())?.value || "";
}

function cleanPreview(snippet: string): string {
  if (!snippet) return "";
  if (looksLikeHtml(snippet)) return stripHtml(snippet);
  return snippet;
}

async function parseGmailError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const message = body?.error?.message;
    if (typeof message === "string" && message.trim()) return message;
  } catch {
    /* ignore */
  }
  return response.statusText || `HTTP ${response.status}`;
}

async function gmailFetch(
  url: string,
  options: { method?: string; body?: string; write?: boolean; retried?: boolean } = {}
): Promise<Response> {
  const { method = "GET", body, write = false, retried = false } = options;
  const storedToken = getStoredToken();
  const requiredScope = write ? GOOGLE_GMAIL_MODIFY_SCOPE : GOOGLE_GMAIL_READONLY_SCOPE;
  const refreshScope = storedToken
    ? await resolveRefreshScope(storedToken, "gmail")
    : write
      ? GOOGLE_GMAIL_SCOPE
      : GOOGLE_GMAIL_READONLY_SCOPE;

  const accessToken = await ensureGoogleAccessToken(storedToken, {
    refreshScope,
    requiredScope,
    preserveTokenOnRefreshFailure: true,
  });

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body,
  });

  if (response.status === 401) {
    if (!retried) {
      const retryScope = await resolveRefreshScope(accessToken, "gmail");
      const refreshed = await requestAccessTokenSilent(retryScope);
      if (refreshed) {
        return gmailFetch(url, { ...options, retried: true });
      }
    }
    throw new GoogleAuthError();
  }

  if (response.status === 403) {
    const detail = await parseGmailError(response);
    throw new Error(detail);
  }

  if (!response.ok) {
    const detail = await parseGmailError(response);
    throw new Error(detail);
  }

  return response;
}

/** Verify Gmail API access after OAuth connect. */
export async function probeGmailAccess(token: string): Promise<void> {
  const response = await fetch(`${GMAIL_API}/users/me/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 401) {
    throw new GoogleAuthError();
  }
  if (!response.ok) {
    const detail = await parseGmailError(response);
    throw new Error(detail);
  }
}

export interface GmailDraftInput {
  to: string;
  subject: string;
  body: string;
  threadId?: string;
  inReplyToMessageId?: string;
}

class GmailService {
  async listMessages(
    params: CommunicationListParams = {}
  ): Promise<{ messages: GmailMessage[]; nextPageToken?: string }> {
    const limit = Math.min(Math.max(params.limit ?? 20, 1), 50);
    const label = LABEL_MAP[params.label || "inbox"] || "INBOX";
    const qs = new URLSearchParams({
      maxResults: String(limit),
      labelIds: label,
    });
    if (params.pageToken) qs.set("pageToken", params.pageToken);

    const q = buildGmailSearchQuery(params);
    if (q) qs.set("q", q);

    const listRes = await gmailFetch(`${GMAIL_API}/users/me/messages?${qs}`);
    const listData = await listRes.json();
    const messages: GmailMessage[] = [];

    for (const m of (listData.messages ?? []).slice(0, limit)) {
      const dRes = await gmailFetch(
        `${GMAIL_API}/users/me/messages/${m.id}?format=metadata&metadataHeaders=Subject&metadataHeaders=From&metadataHeaders=Date`
      );
      const d = await dRes.json();
      const hdrs = d.payload?.headers || [];
      messages.push({
        id: d.id,
        subject: extractHeader(hdrs, "Subject") || "(No subject)",
        from: extractHeader(hdrs, "From"),
        date:
          extractHeader(hdrs, "Date") ||
          new Date(Number(d.internalDate)).toISOString(),
        preview: cleanPreview(d.snippet || ""),
        type: "email",
        read: !d.labelIds?.includes("UNREAD"),
      });
    }

    return { messages, nextPageToken: listData.nextPageToken };
  }

  async getMessage(messageId: string): Promise<GmailMessage> {
    const res = await gmailFetch(
      `${GMAIL_API}/users/me/messages/${encodeURIComponent(messageId)}?format=full`
    );
    const detail = await res.json();
    const headers = detail.payload?.headers || [];
    const parsed = detail.payload ? parseGmailPayload(detail.payload) : { body: "" };
    const fallback = cleanPreview(detail.snippet || "");

    return {
      id: detail.id,
      subject: extractHeader(headers, "Subject") || "(No subject)",
      from: extractHeader(headers, "From"),
      to: extractHeader(headers, "To"),
      date:
        extractHeader(headers, "Date") ||
        new Date(Number(d.internalDate)).toISOString(),
      preview: fallback,
      type: "email",
      read: !detail.labelIds?.includes("UNREAD"),
      body: parsed.body || fallback,
      bodyHtml: parsed.bodyHtml,
    };
  }

  async trashMessage(messageId: string): Promise<void> {
    await gmailFetch(
      `${GMAIL_API}/users/me/messages/${encodeURIComponent(messageId)}/trash`,
      { method: "POST", write: true }
    );
  }

  async archiveMessage(messageId: string): Promise<void> {
    await gmailFetch(`${GMAIL_API}/users/me/messages/${encodeURIComponent(messageId)}/modify`, {
      method: "POST",
      write: true,
      body: JSON.stringify({ removeLabelIds: ["INBOX"] }),
    });
  }

  async createDraft(input: GmailDraftInput): Promise<{ draftId: string }> {
    const lines = [
      `To: ${input.to}`,
      `Subject: ${input.subject}`,
      "Content-Type: text/plain; charset=utf-8",
      "",
      input.body,
    ];
    const raw = btoa(unescape(encodeURIComponent(lines.join("\r\n"))))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");

    const payload: Record<string, unknown> = {
      message: { raw },
    };
    if (input.threadId) {
      (payload.message as Record<string, unknown>).threadId = input.threadId;
    }

    const res = await gmailFetch(`${GMAIL_API}/users/me/drafts`, {
      method: "POST",
      write: true,
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return { draftId: data.id as string };
  }
}

export const gmailService = new GmailService();

export function gmailMessagesQueryKey(
  userId: string | undefined,
  params: CommunicationListParams
) {
  return ["gmail-messages", userId, params] as const;
}

export function gmailMessageQueryKey(
  userId: string | undefined,
  messageId: string | undefined
) {
  return ["gmail-message", userId, messageId] as const;
}

export { GoogleAuthError as GmailAuthError };
